import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function Careers() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://okayama-doboku.com';
  
  useSEO({
    title: '求人情報 - おかやま土木工事株式会社 | 岡山市・倉敷市の土木工事',
    description: 'おかやま土木工事株式会社の求人情報。土木施工管理技士、土木作業員、事務スタッフを募集中。未経験者歓迎、充実の福利厚生、資格取得支援制度あり。岡山市・倉敷市で一緒に働きませんか。',
    keywords: '求人,採用,土木施工管理技士,土木作業員,事務スタッフ,岡山市,倉敷市',
    canonical: '/careers',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "土木施工管理技士・土木作業員・事務スタッフ",
      "description": "おかやま土木工事株式会社では、地域のインフラを支える重要な仕事に携わる仲間を募集しています。",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "おかやま土木工事株式会社",
        "sameAs": siteUrl
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "岡山市",
          "addressRegion": "岡山県",
          "addressCountry": "JP"
        }
      },
      "employmentType": "FULL_TIME"
    }
  });

  const jobOpenings = [
    {
      title: '土木施工管理技士',
      type: '正社員',
      experience: '経験者優遇',
      salary: '月給 25万円〜40万円',
      description: '土木工事現場での施工管理業務をお任せします。工程管理、品質管理、安全管理が主な業務内容です。',
      requirements: [
        '土木施工管理技士資格（1級・2級問わず）',
        '土木工事現場での施工管理経験3年以上',
        '普通自動車免許（AT限定可）',
        'パソコン基本操作（Word、Excel）'
      ],
      benefits: [
        '各種社会保険完備',
        '退職金制度',
        '資格取得支援制度',
        '交通費支給',
        '年2回賞与'
      ]
    },
    {
      title: '土木作業員',
      type: '正社員',
      experience: '未経験歓迎',
      salary: '月給 20万円〜30万円',
      description: '土木工事現場での作業をお任せします。先輩スタッフが丁寧に指導しますので、未経験の方も安心してご応募ください。',
      requirements: [
        '普通自動車免許（AT限定可）',
        '体力に自信のある方',
        'チームワークを大切にできる方',
        '安全意識の高い方'
      ],
      benefits: [
        '各種社会保険完備',
        '退職金制度',
        '資格取得支援制度',
        '交通費支給',
        '作業服貸与',
        '年2回賞与'
      ]
    },
    {
      title: '事務スタッフ',
      type: '正社員・パート',
      experience: '経験者優遇',
      salary: '月給 18万円〜25万円（正社員）/ 時給 900円〜1,200円（パート）',
      description: '一般事務、経理事務、電話対応などの事務業務全般をお任せします。建設業界の知識は入社後に身につけていただけます。',
      requirements: [
        'パソコン基本操作（Word、Excel）',
        '電話対応ができる方',
        '簿記3級以上（経理経験者優遇）',
        '普通自動車免許（AT限定可）'
      ],
      benefits: [
        '各種社会保険完備',
        '退職金制度',
        '資格取得支援制度',
        '交通費支給',
        '年2回賞与（正社員のみ）'
      ]
    }
  ];

  const companyBenefits = [
    {
      icon: 'ri-shield-check-line',
      title: '充実の福利厚生',
      description: '各種社会保険、退職金制度、有給休暇など、安心して働ける環境を整えています。'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: '資格取得支援',
      description: '業務に必要な資格取得を会社が全面的にサポート。受験費用や講習費用を会社が負担します。'
    },
    {
      icon: 'ri-team-line',
      title: 'アットホームな職場',
      description: '少数精鋭のチームで、一人ひとりの意見を大切にするアットホームな職場環境です。'
    },
    {
      icon: 'ri-time-line',
      title: 'ワークライフバランス',
      description: '残業時間の削減に取り組み、プライベートの時間も大切にできる働き方を推進しています。'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-96 bg-cover bg-center" style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/465a1bbf7886983368903807d1eddc7f.jpg')`
        }}>
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="text-white text-center">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">求人情報</h1>
                <p className="text-xl md:text-2xl">
                  一緒に岡山県の未来を築きませんか
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Company Message */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              <strong>私たちと一緒に働きませんか</strong>
            </h2>
            <div className="text-lg text-gray-600 leading-relaxed space-y-4">
              <p>
                おかやま土木工事株式会社では、地域のインフラを支える重要な仕事に携わる仲間を募集しています。
                経験者はもちろん、未経験の方も大歓迎です。
              </p>
              <p>
                私たちは「安全第一、品質第一、お客様第一」をモットーに、
                やりがいのある仕事を通じて地域社会に貢献しています。
              </p>
              <p>
                充実した研修制度と資格取得支援により、あなたのスキルアップを全面的にサポートします。
                アットホームな職場で、一緒に成長していきましょう。
              </p>
            </div>
          </div>
        </section>

        {/* Company Benefits */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              <strong>働く環境・待遇</strong>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {companyBenefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${benefit.icon} text-3xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Job Openings */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              <strong>募集職種</strong>
            </h2>
            
            <div className="space-y-8">
              {jobOpenings.map((job, index) => (
                <div key={index} className="bg-gray-50 rounded-lg shadow-lg overflow-hidden">
                  <div className="p-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{job.title}</h3>
                        <div className="flex flex-wrap gap-2">
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                            {job.type}
                          </span>
                          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                            {job.experience}
                          </span>
                        </div>
                      </div>
                      <div className="mt-4 md:mt-0">
                        <div className="text-2xl font-bold text-blue-600">{job.salary}</div>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-6 leading-relaxed">{job.description}</p>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">応募資格・条件</h4>
                        <ul className="space-y-2">
                          {job.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-start">
                              <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                              <span className="text-gray-700">{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">待遇・福利厚生</h4>
                        <ul className="space-y-2">
                          {job.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start">
                              <i className="ri-star-line text-yellow-500 mr-2 mt-1"></i>
                              <span className="text-gray-700">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Process */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              <strong>応募の流れ</strong>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">お電話・メール</h3>
                <p className="text-gray-600">まずはお気軽にお電話またはメールでご連絡ください</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">面接日程調整</h3>
                <p className="text-gray-600">ご都合の良い日時で面接の日程を調整いたします</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">面接・見学</h3>
                <p className="text-gray-600">面接と職場見学を行います。ご質問もお気軽にどうぞ</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  4
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">採用決定</h3>
                <p className="text-gray-600">採用が決定しましたら、入社日等の詳細をご相談します</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact for Careers */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              <strong>応募・お問い合わせ</strong>
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              ご応募やご質問がございましたら、お気軽にお電話またはメールでお問い合わせください。
              職場見学も随時受け付けております。
            </p>
            
            <div className="bg-gray-50 rounded-lg p-8 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="text-center">
                  <i className="ri-phone-line text-4xl text-blue-600 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">お電話でのお問い合わせ</h3>
                  <div className="text-2xl font-bold text-blue-600 mb-2">086-012-3456</div>
                  <p className="text-gray-600">営業時間：9:00〜18:00（日曜・祝日休み）</p>
                </div>
                
                <div className="text-center">
                  <i className="ri-mail-line text-4xl text-blue-600 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">メールでのお問い合わせ</h3>
                  <p className="text-gray-600 mb-4">
                    下記のお問い合わせフォームより<br />
                    「求人について」を選択してお送りください
                  </p>
                </div>
              </div>
            </div>
            
            <a
              href="/#contact"
              className="inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              お問い合わせフォームへ
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}